import axios from 'axios'
import apiClient from '@/services/api/client'
import { env } from '@/config/env'
import type { StaffUser } from '@/types/staff-user'

export interface StaffLoginPayload {
  email: string
  password: string
}

export interface StaffAuthResponse {
  accessToken: string
  user: StaffUser
  authentication?: { strategy: string; accessToken: string }
  refreshToken?: string
  longLivedToken?: string
}

export interface TokenRefreshResponse {
  accessToken: string
  refreshToken: string
  longLivedToken?: string
  user?: StaffUser
}

const STAFF_AUTH = '/api/v1/authentication/staff'
const AUTH_REFRESH = '/api/v1/auth-refresh'

export const authApi = {
  async loginStaff(payload: StaffLoginPayload): Promise<StaffAuthResponse> {
    const res = await apiClient.post<StaffAuthResponse>(STAFF_AUTH, {
      strategy: 'local',
      email: payload.email,
      password: payload.password,
    })
    return res.data
  },

  async refreshAccessToken(refreshToken: string): Promise<TokenRefreshResponse> {
    // Plain axios: no Bearer header (expired JWT must not hit auth-refresh).
    const res = await axios.post<TokenRefreshResponse>(
      `${env.apiUrl}${AUTH_REFRESH}`,
      { refreshToken },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30_000 },
    )
    return res.data
  },

  async authenticateStaffToken(accessToken: string): Promise<StaffAuthResponse> {
    const res = await apiClient.post<StaffAuthResponse>(STAFF_AUTH, {
      strategy: 'jwt',
      accessToken,
    })
    return res.data
  },

  async logoutStaff(refreshToken?: string): Promise<void> {
    const q = refreshToken
      ? `?refreshToken=${encodeURIComponent(refreshToken)}`
      : ''
    await apiClient.delete(`${STAFF_AUTH}${q}`)
  },

  async requestPasswordReset(email: string, locale: string): Promise<void> {
    await apiClient.post('/api/v1/public/auth-password-reset/request', {
      email,
      locale,
    })
  },

  async confirmPasswordReset(token: string, password: string): Promise<StaffAuthResponse> {
    const res = await apiClient.post<StaffAuthResponse>('/api/v1/public/auth-password-reset/confirm', {
      token,
      password,
    })
    return res.data
  },
}
